import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Inbox, Clock, CheckCircle2, Calendar, FileText, Hammer, MessagesSquare, ArrowRight } from "lucide-react";
import { isObraEmCurso } from "@/pages/admin/AdminObrasEmCurso";

const QUOTE_STATUSES = ["Orçamento em preparação", "Orçamento", "Proposta disponível"];

export default function AdminDashboard() {
    const [stats, setStats] = useState({ leads: 0, newLeads: 0, visits: 0, quotes: 0, won: 0, inProgress: 0 });
    const [recent, setRecent] = useState([]);
    const [awaiting, setAwaiting] = useState({ conversations: 0, clients: 0 });

    useEffect(() => {
        api.get("/admin/leads").then(({ data: ls }) => {
            const waiting = ls.filter((x) => x.status && x.status !== "Novo" && x.awaiting_reply > 0);
            setAwaiting({ conversations: waiting.reduce((n, x) => n + x.awaiting_reply, 0), clients: waiting.length });
            setStats({
                leads: ls.length,
                newLeads: ls.filter(x => !x.status || x.status === "Novo").length,
                visits: ls.filter(x => x.status === "Visita agendada").length,
                quotes: ls.filter(x => QUOTE_STATUSES.includes(x.status)).length,
                won: ls.filter(x => x.status === "Adjudicado").length,
                inProgress: ls.filter(isObraEmCurso).length,
            });
            setRecent(ls.slice(0, 8));
        }).catch(() => {});
    }, []);

    return (
        <div>
            <h1 className="font-display text-3xl font-bold text-stone-900 mb-2">Painel de gestão</h1>
            <p className="text-stone-500 mb-8">Visão geral rápida da atividade comercial.</p>
            <AwaitingReplyCard {...awaiting} />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
                <Stat icon={Inbox} label="Total de pedidos" value={stats.leads} testid="stat-total" />
                <Stat icon={Clock} label="Novos" value={stats.newLeads} highlight testid="stat-novos" />
                <Stat icon={Calendar} label="Visitas" value={stats.visits} testid="stat-visitas" />
                <Stat icon={FileText} label="Orçamentos" value={stats.quotes} testid="stat-orcamentos" />
                <Stat icon={CheckCircle2} label="Adjudicados" value={stats.won} good testid="stat-adjudicados" />
                <Stat icon={Hammer} label="Obras em curso" value={stats.inProgress} testid="stat-obras-em-curso" />
            </div>
            <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h2 className="font-display font-semibold text-lg mb-4">Últimos pedidos</h2>
                {recent.length === 0 ? <p className="text-stone-500 text-sm">Ainda sem pedidos.</p> : (
                    <ul className="divide-y divide-stone-100">
                        {recent.map(l => (
                            <li key={l.id} className="py-3 flex justify-between text-sm">
                                <span><span className="font-medium">{l.name}</span> — {l.location} <span className="text-xs text-stone-500 ml-2">{(l.work_types || []).join(", ")}</span></span>
                                <span className="text-xs bg-stone-100 px-2 py-0.5 rounded-full">{l.status}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function AwaitingReplyCard({ conversations, clients }) {
    const nav = useNavigate();
    const on = conversations > 0;
    const detail = !on
        ? "Nenhum cliente à espera de resposta."
        : `${conversations === 1 ? "1 conversa aguarda" : `${conversations} conversas aguardam`} resposta${clients > 1 ? ` de ${clients} clientes` : ""}.`;
    return (
        <button
            type="button"
            onClick={() => nav("/admin/clientes?filtro=por-responder")}
            className={`group w-full mb-6 flex items-center gap-4 p-4 md:p-5 rounded-xl border text-left transition-colors ${on ? "bg-orange-50 border-[hsl(var(--copper))]/40 hover:bg-orange-100/70" : "bg-white border-stone-200 hover:border-stone-300"}`}
            data-testid="stat-por-responder"
        >
            <span className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center ${on ? "bg-[hsl(var(--copper))] text-white" : "bg-stone-100 text-stone-400"}`}>
                <MessagesSquare className="w-5 h-5" />
            </span>
            <span className="flex-1 min-w-0">
                <span className="block text-[10px] uppercase tracking-wider text-stone-500">Mensagens por responder</span>
                <span className="flex items-baseline gap-3 flex-wrap">
                    <span className="font-display text-2xl font-bold text-stone-900" data-testid="stat-por-responder-value">{conversations}</span>
                    <span className="text-sm text-stone-600" data-testid="stat-por-responder-detail">{detail}</span>
                </span>
            </span>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--copper))]">
                Ver clientes <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </span>
            <ArrowRight className="sm:hidden w-4 h-4 text-[hsl(var(--copper))]" />
        </button>
    );
}

function Stat({ icon: Icon, label, value, highlight, good, testid }) {
    const cls = highlight ? "bg-amber-50 border-amber-200" : good ? "bg-emerald-50 border-emerald-200" : "bg-white border-stone-200";
    const iconCls = highlight ? "text-[#B56D3B]" : good ? "text-emerald-700" : "text-stone-400";
    return (
        <div className={`p-4 rounded-xl border ${cls}`} data-testid={testid}>
            <Icon className={`w-4 h-4 mb-2 ${iconCls}`} />
            <div className="font-display text-2xl font-bold text-stone-900" data-testid={`${testid}-value`}>{value}</div>
            <div className="text-[10px] uppercase tracking-wider text-stone-500 mt-1">{label}</div>
        </div>
    );
}
